/**
 * new-game-setup.ts — full-screen DOM overlay for new-game setup (v2M1).
 *
 * Per UI_UX §9.2. Two-column form: Galaxy settings left (system count, seed)
 * and Empires right (player race + AI opponent slots). Start Game collects
 * the chosen options and hands them to the session bootstrapper.
 *
 * Multi-empire same-race is allowed — AI slot dropdowns don't filter out
 * the player's race, and slots don't filter each other. 'Random' slots
 * resolve through the seeded RNG inside scenario.ts so the full new-game
 * state is reproducible from (seed, settings).
 */

import type { LoadedGameData } from '../data/loader';
import type { AiOpponentSpec, NewGameOptions } from '../game/scenario';
import type { AiBehaviour, AiDifficulty, RaceId } from '../state/types';

export interface NewGameSetupCallbacks {
  onStart(options: NewGameOptions): void;
  onBack(): void;
}

export interface NewGameSetupHandle {
  destroy(): void;
}

const SYSTEM_COUNT_MIN = 6;
const SYSTEM_COUNT_MAX = 30;
const SYSTEM_COUNT_DEFAULT = 12;
const AI_COUNT_MAX = 5;
const AI_COUNT_DEFAULT = 2;

/**
 * A random 32-bit unsigned integer, rendered as hex. Uses Math.random —
 * seed generation itself does not need to be deterministic, only gameplay
 * after the seed is fixed.
 */
function randomSeedHex(): string {
  const n = Math.floor(Math.random() * 0x100000000);
  return '0x' + n.toString(16).toUpperCase().padStart(8, '0');
}

/** Parse a seed input. Accepts decimal or 0x-prefixed hex. Returns null on invalid. */
function parseSeedInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = trimmed.toLowerCase().startsWith('0x')
    ? parseInt(trimmed.slice(2), 16)
    : Number(trimmed);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return null;
  return n >>> 0; // force unsigned 32-bit
}

export function mountNewGameSetup(
  parent: HTMLElement,
  data: LoadedGameData,
  callbacks: NewGameSetupCallbacks,
): NewGameSetupHandle {
  // --- state -------------------------------------------------------------
  const v1Races = data.races.filter((r) => r.status === 'v1');
  const defaultPlayerRace = (v1Races[0]?.raceId ?? 'RACE-AA') as RaceId;

  // Drive the dropdown options from the loaded AI data so the xlsx
  // remains the single authoring surface. We retain a canonical display
  // order (Easy/Normal/Hard, behaviours per design lock) and intersect
  // with the keys actually present in the data.
  const difficultyOrder: readonly AiDifficulty[] = ['Easy', 'Normal', 'Hard'];
  const orderedDifficulties: AiDifficulty[] = difficultyOrder.filter(
    (k) => k in data.aiBehaviours.difficulties,
  );
  const behaviourOrder: readonly AiBehaviour[] = [
    'Expansionist',
    'Economist',
    'Technologist',
    'Fortifier',
    'Aggressive',
    'Balanced',
  ];
  const behaviourOptions: AiBehaviour[] = behaviourOrder.filter(
    (b) => b in data.aiBehaviours.behaviours,
  );

  interface AiSlotChoice {
    race: RaceId | 'random';
    difficulty: AiDifficulty;
    behaviour: AiBehaviour;
  }

  let seedText = randomSeedHex();
  let systemCount = SYSTEM_COUNT_DEFAULT;
  let playerRace: RaceId = defaultPlayerRace;
  let aiCount = AI_COUNT_DEFAULT;
  const aiSlotChoices: AiSlotChoice[] = new Array(AI_COUNT_MAX)
    .fill(null)
    .map(() => ({
      race: 'random',
      difficulty: 'Normal',
      behaviour: 'Balanced',
    }));

  // --- overlay skeleton --------------------------------------------------
  const overlay = document.createElement('div');
  overlay.id = 'new-game-setup-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    background: '#05060A',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: '48px 24px 24px',
    zIndex: '1000',
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    color: '#D7DEEA',
    overflowY: 'auto',
  } as Partial<CSSStyleDeclaration>);

  const title = document.createElement('div');
  title.textContent = 'NEW GAME';
  Object.assign(title.style, {
    fontSize: '28px',
    letterSpacing: '0.15em',
    fontWeight: '600',
    marginBottom: '32px',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(title);

  const columnsWrap = document.createElement('div');
  Object.assign(columnsWrap.style, {
    display: 'flex',
    gap: '48px',
    width: '100%',
    // v3M7.2.1 — widened from 820 → 1040 so the three-dropdown AI slot
    // row (Race / Difficulty / Behaviour) has room without label clipping.
    maxWidth: '1040px',
    justifyContent: 'center',
    alignItems: 'flex-start',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(columnsWrap);

  // --- column factory ----------------------------------------------------
  function makeColumn(heading: string): HTMLElement {
    const col = document.createElement('div');
    Object.assign(col.style, {
      flex: '1 1 0',
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      padding: '20px',
      background: '#0E131C',
      border: '1px solid #2A3344',
      borderRadius: '2px',
      minWidth: '320px',
    } as Partial<CSSStyleDeclaration>);
    const h = document.createElement('div');
    h.textContent = heading;
    Object.assign(h.style, {
      fontSize: '14px',
      letterSpacing: '0.12em',
      color: '#7E8799',
      textTransform: 'uppercase',
      marginBottom: '4px',
    } as Partial<CSSStyleDeclaration>);
    col.appendChild(h);
    columnsWrap.appendChild(col);
    return col;
  }

  // --- label helpers -----------------------------------------------------
  function makeRow(label: string, control: HTMLElement): HTMLElement {
    const row = document.createElement('label');
    Object.assign(row.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      fontSize: '13px',
      color: '#D7DEEA',
    } as Partial<CSSStyleDeclaration>);
    const lbl = document.createElement('span');
    lbl.textContent = label;
    lbl.style.color = '#7E8799';
    row.appendChild(lbl);
    row.appendChild(control);
    return row;
  }

  function styleInput(el: HTMLElement): void {
    Object.assign(el.style, {
      background: '#05060A',
      color: '#D7DEEA',
      border: '1px solid #2A3344',
      borderRadius: '2px',
      padding: '8px 10px',
      fontFamily: 'JetBrains Mono, ui-monospace, monospace',
      fontSize: '13px',
    } as Partial<CSSStyleDeclaration>);
  }

  // --- Galaxy column -----------------------------------------------------
  const galaxyCol = makeColumn('Galaxy');

  // System count slider
  const systemCountVal = document.createElement('span');
  systemCountVal.textContent = String(systemCount);
  const systemCountSlider = document.createElement('input');
  systemCountSlider.type = 'range';
  systemCountSlider.min = String(SYSTEM_COUNT_MIN);
  systemCountSlider.max = String(SYSTEM_COUNT_MAX);
  systemCountSlider.value = String(systemCount);
  systemCountSlider.style.width = '100%';
  systemCountSlider.addEventListener('input', () => {
    systemCount = Number(systemCountSlider.value);
    systemCountVal.textContent = String(systemCount);
  });
  const systemCountRow = makeRow('Systems', systemCountSlider);
  const sysLabelSpan = systemCountRow.querySelector('span')!;
  sysLabelSpan.textContent = 'Systems';
  sysLabelSpan.appendChild(document.createTextNode('  '));
  sysLabelSpan.appendChild(systemCountVal);
  (systemCountVal.style as unknown as Record<string, string>).color = '#D7DEEA';
  galaxyCol.appendChild(systemCountRow);

  // Seed input + randomise
  const seedWrap = document.createElement('div');
  Object.assign(seedWrap.style, {
    display: 'flex',
    gap: '8px',
  } as Partial<CSSStyleDeclaration>);
  const seedInput = document.createElement('input');
  seedInput.type = 'text';
  seedInput.value = seedText;
  styleInput(seedInput);
  seedInput.style.flex = '1';
  seedInput.addEventListener('input', () => {
    seedText = seedInput.value;
  });
  const randomiseBtn = document.createElement('button');
  randomiseBtn.type = 'button';
  randomiseBtn.textContent = 'Randomise';
  styleInput(randomiseBtn);
  randomiseBtn.style.cursor = 'pointer';
  randomiseBtn.addEventListener('click', () => {
    seedText = randomSeedHex();
    seedInput.value = seedText;
  });
  seedWrap.appendChild(seedInput);
  seedWrap.appendChild(randomiseBtn);
  galaxyCol.appendChild(makeRow('Seed (hex or decimal)', seedWrap));

  // --- Empires column ----------------------------------------------------
  // v3M7.2.1 — widen the empires column relative to the galaxy column so
  // the three-dropdown AI slot row fits without clipping.
  const empiresCol = makeColumn('Empires');
  Object.assign(empiresCol.style, {
    flex: '2 1 0',
    minWidth: '560px',
  } as Partial<CSSStyleDeclaration>);

  // Player race dropdown
  const playerSelect = document.createElement('select');
  styleInput(playerSelect);
  for (const r of v1Races) {
    const opt = document.createElement('option');
    opt.value = r.raceId;
    opt.textContent = r.name;
    if (r.raceId === playerRace) opt.selected = true;
    playerSelect.appendChild(opt);
  }
  playerSelect.addEventListener('change', () => {
    playerRace = playerSelect.value as RaceId;
  });
  empiresCol.appendChild(makeRow('Your race', playerSelect));

  // AI opponent count slider
  const aiCountVal = document.createElement('span');
  aiCountVal.textContent = String(aiCount);
  const aiCountSlider = document.createElement('input');
  aiCountSlider.type = 'range';
  aiCountSlider.min = '0';
  aiCountSlider.max = String(AI_COUNT_MAX);
  aiCountSlider.value = String(aiCount);
  aiCountSlider.style.width = '100%';
  const aiCountRow = makeRow('AI opponents', aiCountSlider);
  const aiLabelSpan = aiCountRow.querySelector('span')!;
  aiLabelSpan.textContent = 'AI opponents';
  aiLabelSpan.appendChild(document.createTextNode('  '));
  aiLabelSpan.appendChild(aiCountVal);
  (aiCountVal.style as unknown as Record<string, string>).color = '#D7DEEA';
  empiresCol.appendChild(aiCountRow);

  // Per-slot race dropdowns — show/hide based on aiCount.
  const aiSlotsWrap = document.createElement('div');
  Object.assign(aiSlotsWrap.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  } as Partial<CSSStyleDeclaration>);
  empiresCol.appendChild(aiSlotsWrap);

  /**
   * Wrap a select with a tiny subhead label so the user can tell which
   * column is which at a glance. v3M7.2.1.
   */
  function makeTaggedSelect(tag: string, sel: HTMLSelectElement): HTMLElement {
    const wrap = document.createElement('div');
    Object.assign(wrap.style, {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    } as Partial<CSSStyleDeclaration>);
    const subhead = document.createElement('span');
    subhead.textContent = tag;
    Object.assign(subhead.style, {
      fontSize: '10px',
      letterSpacing: '0.10em',
      color: '#7E8799',
      textTransform: 'uppercase',
    } as Partial<CSSStyleDeclaration>);
    wrap.appendChild(subhead);
    wrap.appendChild(sel);
    return wrap;
  }

  const slotRowEls: HTMLElement[] = [];
  for (let i = 0; i < AI_COUNT_MAX; i++) {
    const slotIndex = i;

    // Three side-by-side selects: race / difficulty / behaviour.
    const tripletWrap = document.createElement('div');
    Object.assign(tripletWrap.style, {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gap: '8px',
    } as Partial<CSSStyleDeclaration>);

    const raceSel = document.createElement('select');
    styleInput(raceSel);
    const randomOpt = document.createElement('option');
    randomOpt.value = 'random';
    randomOpt.textContent = 'Random';
    raceSel.appendChild(randomOpt);
    for (const r of v1Races) {
      const opt = document.createElement('option');
      opt.value = r.raceId;
      opt.textContent = r.name;
      raceSel.appendChild(opt);
    }
    raceSel.value = aiSlotChoices[slotIndex]!.race;
    raceSel.addEventListener('change', () => {
      aiSlotChoices[slotIndex]!.race = raceSel.value as RaceId | 'random';
    });

    const diffSel = document.createElement('select');
    styleInput(diffSel);
    for (const d of orderedDifficulties) {
      const opt = document.createElement('option');
      opt.value = d;
      opt.textContent = d;
      diffSel.appendChild(opt);
    }
    diffSel.value = aiSlotChoices[slotIndex]!.difficulty;
    diffSel.addEventListener('change', () => {
      aiSlotChoices[slotIndex]!.difficulty = diffSel.value as AiDifficulty;
    });

    const behaveSel = document.createElement('select');
    styleInput(behaveSel);
    for (const b of behaviourOptions) {
      const opt = document.createElement('option');
      opt.value = b;
      opt.textContent = b;
      behaveSel.appendChild(opt);
    }
    behaveSel.value = aiSlotChoices[slotIndex]!.behaviour;
    behaveSel.addEventListener('change', () => {
      aiSlotChoices[slotIndex]!.behaviour = behaveSel.value as AiBehaviour;
    });

    tripletWrap.appendChild(makeTaggedSelect('Race', raceSel));
    tripletWrap.appendChild(makeTaggedSelect('Difficulty', diffSel));
    tripletWrap.appendChild(makeTaggedSelect('Behaviour', behaveSel));

    const row = makeRow(`AI opponent ${i + 1}`, tripletWrap);
    aiSlotsWrap.appendChild(row);
    slotRowEls.push(row);
  }

  const syncSlotVisibility = (): void => {
    for (let i = 0; i < AI_COUNT_MAX; i++) {
      slotRowEls[i]!.style.display = i < aiCount ? '' : 'none';
    }
  };
  aiCountSlider.addEventListener('input', () => {
    aiCount = Number(aiCountSlider.value);
    aiCountVal.textContent = String(aiCount);
    syncSlotVisibility();
  });
  syncSlotVisibility();

  // --- footer actions ----------------------------------------------------
  const footer = document.createElement('div');
  Object.assign(footer.style, {
    display: 'flex',
    gap: '12px',
    marginTop: '32px',
    justifyContent: 'center',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(footer);

  function makeActionButton(label: string, primary: boolean): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = label;
    Object.assign(btn.style, {
      padding: '12px 32px',
      background: primary ? '#2B4A7A' : '#0E131C',
      color: primary ? '#FFFFFF' : '#D7DEEA',
      border: `1px solid ${primary ? '#3F6FA8' : '#2A3344'}`,
      borderRadius: '2px',
      fontFamily: 'inherit',
      fontSize: '14px',
      letterSpacing: '0.08em',
      cursor: 'pointer',
    } as Partial<CSSStyleDeclaration>);
    return btn;
  }

  const backBtn = makeActionButton('Back', false);
  backBtn.addEventListener('click', callbacks.onBack);
  footer.appendChild(backBtn);

  const startBtn = makeActionButton('Start Game', true);
  footer.appendChild(startBtn);

  // --- error display (for invalid seed etc.) -----------------------------
  const errorLine = document.createElement('div');
  Object.assign(errorLine.style, {
    color: '#C84A4A',
    fontSize: '12px',
    marginTop: '12px',
    minHeight: '16px',
    textAlign: 'center',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(errorLine);

  startBtn.addEventListener('click', () => {
    const seedN = parseSeedInput(seedText);
    if (seedN === null) {
      errorLine.textContent =
        'Invalid seed — enter a decimal integer or 0x-prefixed hex.';
      return;
    }
    errorLine.textContent = '';
    const aiOpponents: AiOpponentSpec[] = [];
    for (let i = 0; i < aiCount; i++) {
      const choice = aiSlotChoices[i]!;
      aiOpponents.push({
        raceId: choice.race,
        difficulty: choice.difficulty,
        behaviour: choice.behaviour,
      });
    }
    callbacks.onStart({
      seed: seedN,
      playerRace,
      systemCount,
      aiOpponents,
    });
  });

  parent.appendChild(overlay);

  return {
    destroy(): void {
      overlay.remove();
    },
  };
}
